/**
 * Connection store for WebSocket state management.
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import type { ConnectionStatus } from '../types/websocket';

// =============================================================================
// TYPES
// =============================================================================

interface ConnectionStoreState {
  // Connection status
  status: ConnectionStatus;

  // Client ID assigned by server
  clientId: string | null;

  // Reconnection tracking
  reconnectAttempts: number;
  nextReconnectTime: number | null;  // Timestamp for countdown display

  // Heartbeat tracking
  lastHeartbeat: number | null;

  // Current viewport subscription
  currentViewport: [number, number, number, number] | null;

  // Error state
  lastError: string | null;

  // Actions
  setStatus: (status: ConnectionStatus) => void;
  setClientId: (clientId: string | null) => void;
  incrementReconnectAttempts: () => void;
  resetReconnectAttempts: () => void;
  setNextReconnectTime: (time: number | null) => void;
  setLastHeartbeat: (time: number) => void;
  setCurrentViewport: (bbox: [number, number, number, number] | null) => void;
  setLastError: (error: string | null) => void;
  reset: () => void;
}

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState = {
  status: 'disconnected' as ConnectionStatus,
  clientId: null,
  reconnectAttempts: 0,
  nextReconnectTime: null,
  lastHeartbeat: null,
  currentViewport: null,
  lastError: null,
};

// =============================================================================
// STORE
// =============================================================================

export const useConnectionStore = create<ConnectionStoreState>()(
  immer((set) => ({
    ...initialState,

    setStatus: (status) => {
      set((state) => {
        state.status = status;
        if (status === 'connected') {
          state.lastError = null;
        }
      });
    },

    setClientId: (clientId) => {
      set((state) => {
        state.clientId = clientId;
      });
    },

    incrementReconnectAttempts: () => {
      set((state) => {
        state.reconnectAttempts += 1;
      });
    },

    resetReconnectAttempts: () => {
      set((state) => {
        state.reconnectAttempts = 0;
        state.nextReconnectTime = null;
      });
    },

    setNextReconnectTime: (time) => {
      set((state) => {
        state.nextReconnectTime = time;
      });
    },

    setLastHeartbeat: (time) => {
      set((state) => {
        state.lastHeartbeat = time;
      });
    },

    setCurrentViewport: (bbox) => {
      set((state) => {
        state.currentViewport = bbox;
      });
    },

    setLastError: (error) => {
      set((state) => {
        state.lastError = error;
      });
    },

    reset: () => {
      set(() => initialState);
    },
  }))
);

// =============================================================================
// SELECTORS
// =============================================================================

export const selectConnectionStatus = (state: ConnectionStoreState) => state.status;
export const selectClientId = (state: ConnectionStoreState) => state.clientId;
export const selectReconnectAttempts = (state: ConnectionStoreState) => state.reconnectAttempts;
export const selectNextReconnectTime = (state: ConnectionStoreState) => state.nextReconnectTime;
export const selectLastHeartbeat = (state: ConnectionStoreState) => state.lastHeartbeat;
export const selectCurrentViewport = (state: ConnectionStoreState) => state.currentViewport;
export const selectLastError = (state: ConnectionStoreState) => state.lastError;
export const selectIsConnected = (state: ConnectionStoreState) => state.status === 'connected';
